<template>
    <div class="terms-of-service">
        <p class="copy">Now, read and accept the <em>Terms of Service</em>:</p>

        <div
            class="terms-of-service__body"
            @scroll.passive="checkTermsRead"
        >
            <h1>Terms of Use</h1>

            <p>
                goTenna, Inc. ("<strong>goTenna</strong>", "<strong>we,</strong>" "<strong>our,</strong>" or
                "<strong>us</strong>") has established the goTenna Management Portal located at
                <a href="https://portal.gotenna.com">portal.gotenna.com</a> (the "<strong>Portal</strong>") in order to
                provide users of the goTenna Pro&trade; mesh networking radio device (the "<strong>Device</strong>")
                with (a) the ability to set the radio frequencies of their Devices using our proprietary radio
                programming software (the "<strong>RP Software</strong>") and (b) access to certain services associated
                with the Device ("<strong>Services</strong>"), some of which are optional and are offered on a paid
                subscription basis ("<strong>Premium Services</strong>"). The Devices are not usable unless the user
                first accesses the Portal and use the RP Software in order to set radio frequencies for the Devices, and
                enable certain Services which will allow uploading of Device data to the Portal, online storage of the
                user’s data, and the ability to manage and share the user’s data.
            </p>

            <p>
                These Terms of Use and any additional terms that are incorporated herein by reference (collectively, the
                "<strong>Agreement</strong>") govern any access to and use of the Portal, the RP Software and the
                Services by you ("<strong>Customer,</strong>" "<strong>you,</strong>" or "<strong>your</strong>") and
                your Authorized Users (as defined below).
            </p>

            <p>
                If you are entering into the Agreement on behalf of a legal entity, you represent that you have the
                authority to bind such entity to the Agreement, in which case the terms "<strong>you</strong>" or
                "<strong>your</strong>" refers to such entity. Customer and goTenna are each referred to herein as a
                "<strong>Party,</strong>" and together are referred to herein as the "<strong>Parties.</strong>" BY
                CLICKING THE "ACCEPT" BUTTON, YOU ACKNOWLEDGE THAT YOU HAVE REVIEWED AND AGREE TO BE LEGALLY BOUND BY
                THIS AGREEMENT. IF YOU DO NOT ACCEPT THE TERMS OF THIS AGREEMENT, OR DO NOT HAVE THE AUTHORITY TO BIND
                THE ENTITY TO THIS AGREEMENT, YOU AND YOUR AUTHORIZED USERS MAY NOT ACCESS OR USE THE PORTAL, THE RP
                SOFTWARE OR THE SERVICES, AND YOU WILL NOT BE ABLE TO USE YOUR DEVICES.
            </p>

            <ul>
                <li>
                    <em>1. Additional Terms.</em>
                    <ul>
                        <li>
                            <strong>1.1. App Terms.</strong> We may from time to time make available certain software
                            applications available for use with your Devices which are governed by separate terms and
                            conditions provided with those apps ("<strong>Apps</strong>"). Your use of any Apps will be
                            subject to the terms and conditions provided with those Apps, which terms are hereby
                            incorporated into the Agreement and made a part hereof by reference.
                        </li>
                        <li>
                            <strong>1.2. Additional Terms for Premium Services.</strong> Your access to and use of
                            Premium Services may be subject to additional terms and conditions set forth in (a) an
                            online or other order form that is submitted by you and accepted by us with respect to your
                            subscription to such Premium Services (an "<strong>Order Form</strong>") and/or (b) a
                            supplemental terms addendum provided to you by goTenna when you purchase a subscription to
                            such Premium Services (a "<strong>Premium Service Addendum</strong>"). The terms of any
                            Order Forms and Premium Service Addenda are hereby incorporated into the Agreement and made
                            a part hereof by reference.
                        </li>
                        <li>
                            <strong>1.3. Privacy Policy.</strong> Any personal information that is data that is uploaded
                            to the Platform or otherwise provided to or collected by us via the Services will used and
                            disclosed by us in accordance with our privacy policy at
                            <a href="https://gotenna.com/pages/privacy-policy-apps">
                                gotenna.com/pages/privacy-policy-apps
                            </a>
                            (the "<strong>Privacy Policy</strong>"), which is hereby incorporated into the Agreement and
                            made a part hereof by reference; provided, however, that notwithstanding anything to the
                            contrary in the Privacy Policy, we will not provide any such personal information to third
                            parties for their marketing purposes.
                        </li>
                    </ul>
                </li>
            </ul>

            <ul>
                <li>
                    <em>2. RP Software and Services.</em>
                    <ul>
                        <li>
                            <strong>2.1. Access to the RP Software.</strong> Subject to the terms and conditions of the
                            Agreement, goTenna hereby grants you a limited, non-exclusive, non-transferable (except to
                            an acquirer of your business) right to access and use the RP Software via the Portal for the
                            sole purpose of setting radio frequencies for your Devices in accordance with all applicable
                            laws, regulations and rules, and provided that you have obtained all required licenses to
                            use such radio frequencies from the Federal Communications Commission
                            ("<strong>FCC</strong>") and any other applicable governmental authorities. Your access to
                            the RP Software will be provided through the Portal on a software-as-a-service basis; you
                            will not be provided with a downloadable copy of the RP Software. You are solely responsible
                            for complying with all applicable laws, regulations and rules governing your Devices’ use of
                            the radio frequencies that you set for your Devices, and for maintaining all required FCC
                            and other licenses to use such radio frequencies.
                        </li>
                        <li>
                            <strong>2.2. Access to the Services.</strong> Subject to the terms and conditions of the
                            Agreement, goTenna hereby grants you a limited, non-exclusive, non-transferable (except to
                            an acquirer of your business) right to access and use for your internal purposes (a) any
                            Premium Services that are included in your then-current Premium Service subscription and (b)
                            any other Services that goTenna makes available to Device users at no additional charge.
                        </li>
                        <li>
                            <strong>2.3. Beta Features.</strong> From time to time, we may invite you to try “beta”
                            features or functionalities of the Portal, the Services or the RP Software which are not
                            generally available to our customers for production use at no charge. You may accept or
                            decline any such trial in your sole discretion. Such beta features are for evaluation
                            purposes only and not for production use, are not considered part of the products and
                            services provided by us under the Agreement, are not supported, and may be subject to
                            additional terms. Unless otherwise expressly agreed to by us, any beta feature trial period
                            will expire upon the date that a version of the beta feature becomes generally available to
                            all of our customers for production use or upon the date that we elect to discontinue such
                            beta feature. We may discontinue beta features at any time in our sole discretion and may
                            never make them generally available as part of the Portal, the Services or the RP Software.
                            We will have no liability to you or any third party for any harm or damage arising out of or
                            in connection with any use of a beta feature, and your use of any beta feature is at your
                            own risk.
                        </li>
                        <li>
                            <strong>2.4. Authorized Users.</strong> Your access to the Portal and the Services will be
                            limited to your employees, contractors, or agents authorized by you to access and use the
                            Portal and the Services pursuant to the terms and conditions of the Agreement, subject to
                            any user limitations that we communicate to you (including, for example, limitations on the
                            number of users that have administrator logins to the Portal or Services) ("
                            <strong>Authorized Users</strong>"). You are responsible for the acts and omissions of your
                            Authorized Users and any other person who access and uses the Portal, the Services or the RP
                            Software using any Authorized User’s Portal log-in credentials.
                        </li>
                        <li>
                            <strong>2.5. Restrictions on Use.</strong> You will not (and will not authorize, permit, or
                            encourage any third party to): (i) allow anyone other than Authorized Users to access and
                            use the Portal, the Services or the RP Software; (ii) allow an Authorized User to share with
                            any other Authorized User or other person his or her Portal log-in credentials (without
                            limiting the foregoing, an Authorized User who is issued an administrator login to the
                            Portal or the Services may not share that login with any other Authorized User or other
                            person); (iii) reverse engineer, decompile, disassemble, or otherwise attempt to discern the
                            source code or interface protocols of the Services or the RP Software; (iv) modify, adapt,
                            or translate the Services or the RP Software; (v) make any copies of the Services or the RP
                            Software; (vi) resell, distribute, or sublicense the Services or the RP Software or use
                            either of the foregoing for the benefit of anyone other than you or the Authorized Users;
                            (vii) remove or modify any proprietary markings or restrictive legends placed on the
                            Services or the RP Software; (viii) use the Services or the RP Software in violation of any
                            applicable law, rule, or regulation, in order to build a competitive product or service, or
                            for any purpose not specifically permitted in the Agreement; (ix) introduce, post, or upload
                            to the Portal or the Services any Harmful Code (as defined below); (x) use the Portal or the
                            Services in a manner that, in our sole judgement, degrades the reliability, speed, or
                            operation of the Portal or the Services; or (xi) attempt to circumvent any integration
                            between our SDK and the Portal or the Services or any user or other technical controls of
                            the Portal or the Services. As used herein, "<strong>Harmful Code</strong>" means computer
                            code, programs, or programming devices that are intentionally designed to disrupt, modify,
                            access, delete, damage, deactivate, disable, harm, or otherwise impede in any manner,
                            including aesthetic disruptions or distortions, the operation of the Portal or the Services,
                            or any other associated software, firmware, hardware, computer system, or network
                            (including, without limitation, “Trojan horses,” “viruses,” “worms,” “time bombs,” “time
                            locks,” “devices,” “traps,” “access codes,” or “drop dead” or “trap door” devices) or any
                            other harmful, malicious, or hidden procedures, routines or mechanisms that would cause the
                            Services to cease functioning or to damage or corrupt data, storage media, programs,
                            equipment, or communications, or otherwise interfere with the operations of the Portal or
                            the Services. Any breach of this Section 2.5 by Customer shall be deemed to be a material
                            breach of the Agreement an entitle us to exercise our termination rights hereunder.
                        </li>
                        <li>
                            <strong>2.6. Availability.</strong> We will use commercially reasonable efforts to minimize
                            any downtime of the Portal and any Services, other than for scheduled maintenance or
                            downtime caused by reasons beyond our reasonable control, including, but not limited to,
                            acts of God, acts of any governmental body, war, insurrection, sabotage, armed conflict,
                            terrorism, embargo, fire, flood, strike or other labor disturbance, unavailability of or
                            interruption or delay in telecommunications or third-party services, or virus attacks or
                            hackers.
                        </li>
                        <li>
                            <strong>2.7. Modifications.</strong> We reserve the right to modify the Portal, the Services
                            and the RP Software from time to time by adding, deleting, or modifying features to improve
                            the user experience or for other business purposes. We further reserve the right to
                            discontinue any feature of the Portal, the Services or the RP Software or any portion of any
                            of the foregoing, at any time at our sole and reasonable discretion. However, any such
                            modification or discontinuance will not materially decrease the overall functionality of the
                            Portal, the Services or the RP Software.
                        </li>
                    </ul>
                </li>
            </ul>

            <ul>
                <li>
                    <em>3. Premium Service Subscriptions.</em>
                    <ul>
                        <li>
                            <strong>3.1. Order Forms.</strong> If you wish to purchase a subscription to any Premium
                            Services, the specific Premium Services package selected, the applicable fees (the “Premium
                            Subscription Fees”), the subscription term and other details regarding such Premium Services
                            will be set forth in the Order Form.
                        </li>
                        <li>
                            <strong>3.2. Premium Subscription Term.</strong> Unless otherwise set forth on the
                            applicable Order Form, each Premium Services subscription will be for an initial term of one
                            (1) year. Upon expiration of the initial term or renewal term, the term of each Premium
                            Services subscription will automatically renewal for consecutive, subsequent renewal terms
                            of one (1) year each, unless either Party provides the other Party with written notice of
                            non-renewal at least 30 days before the end of the then-current term. Each Premium Services
                            subscription may be terminated early as set forth in Section 4 below and for no other
                            reason.
                        </li>
                        <li>
                            <strong>3.3. Premium Subscription Fees.</strong> If you purchase a subscription to any
                            Premium Services, you agree to pay to goTenna all applicable Premium Subscription Fees.
                            Unless otherwise set forth on the applicable Order Form, all Premium Subscription Fees will
                            be invoiced annually in advance, and will be paid within 30 days of the invoice date.
                            Payments of Premium Subscription Fees will be paid in U.S. dollars. Notwithstanding anything
                            to the contrary, we reserve the right to increase the Premium Subscription Fees that will
                            apply to a renewal term for a Premium Services subscription. We will notify you of such
                            increase when we invoice such Premium Subscription Fees to you. If you do not agree to such
                            fee increase, you may decline to pay such invoice, in which case your Premium Services
                            subscription will terminate at the end of the term that you have paid for.
                        </li>
                        <li>
                            <strong>3.4. Non-Refundable.</strong> Unless otherwise expressly provided for in the
                            Agreement, (i) all Premium Subscription Fees are based on Premium Service subscriptions
                            purchased and not on actual use; and (ii) all Premium Subscription Fees paid under the
                            Agreement are non-refundable.
                        </li>
                        <li>
                            <strong>3.5. Taxes.</strong> Premium Subscription Fees and other charges described herein
                            and in any Order Form are in addition to and do not include any federal, provincial, or
                            local sales, PST, GST, HST, VAT, foreign withholding, use, property, excise, service, or
                            similar transaction taxes (“Taxes”) now or hereafter levied, all of which will be for
                            Customer’s account. If we are required to collect and remit Taxes on your behalf, we will
                            invoice you for such Taxes, and you will pay us for such amounts in accordance with the
                            payment terms set forth herein (including by automatic payment). Customer hereby agrees to
                            indemnify, defend, and hold goTenna, our Affiliates (as defined below), our suppliers and
                            hosting providers, and our and their respective officers, directors, members, managers,
                            partners, employees, contractors, and agents harmless from and against any and all
                            liabilities, costs, and expenses (including reasonable attorneys’ fees) incurred by any such
                            parties in connection with any Taxes and related costs, interest, and penalties paid or
                            payable by goTenna on your behalf. For the avoidance of doubt, we will only be responsible
                            for taxes related to our income,property,franchise,oremployees.
                            “Affiliate”means,withrespecttoanyParty,anyperson or entity which, directly or indirectly,
                            controls, is controlled by, or is under common control with, the specified Party. For the
                            purposes of this definition, the term “control” means the possession, directly or
                            indirectly, of the power to direct or cause the direction of the management of that entity,
                            whether through ownership of voting securities or otherwise.
                        </li>
                    </ul>
                </li>
            </ul>

            <ul>
                <li>
                    <em>4. Termination.</em>
                    <ul>
                        <li>
                            <strong>4.1. Term.</strong> The Agreement will remain in effect at all times unless and
                            until terminate in accordance with <span class="underline">Section 4.2</span> below.
                        </li>
                        <li>
                            <strong>4.2. Termination.</strong>
                            <ul>
                                <li>
                                    <strong>4.2.1.Termination for Convenience.</strong> Provided that no Premium
                                    Services subscription is in effect for you at the time, either Party may terminate
                                    the Agreement for convenience at any time upon written notice to the other Party.
                                </li>
                                <li>
                                    <strong>4.2.2.Premium Services Subscriptions.</strong> Either Party may terminate a
                                    Premium Services subscription upon ten (10) business days’ prior, written notice to
                                    the other Party if the other Party commits a material breach of the Agreement
                                    related to such Premium Services subscription and such breach is not cured within
                                    such ten (10) business day period. In addition, we may terminate a Premium Services
                                    subscription at any time for convenience upon thirty (30) days prior, written notice
                                    to you; provided, however, that if we terminate a Premium Services subscription for
                                    convenience, we will refund to you a pro rata portion of the Premium Subscription
                                    Fees paid by you for the then-current annual Premium Services subscription term,
                                    such portion to be pro rated based on the number of days following the effective
                                    date of such termination and the date on which the then-current annual Premium
                                    Services subscription term would have expired if the Premium Services subscription
                                    had not been terminated early.
                                </li>
                            </ul>
                        </li>
                        <li>
                            <strong>4.3. Effect of Termination.</strong> Upon termination of the Agreement (in whole or
                            in part) as set forth in <span class="underline">Section 4.2</span>, we will stop providing
                            access to, and you will (and will cause your Authorized Users to) immediately cease all use
                            of, the Portal, the Services and the RF Software (or, in the case of termination under
                            <span class="underline">Section 4.2.2</span>, we will stop providing access to, and you will
                            (and will cause your Authorized Users to) immediately cease all use of, the terminated
                            Premium Services).
                        </li>
                        <li>
                            <strong>4.4. Survival.</strong> The following provisions will survive termination of the
                            Agreement: <span class="underline">Section 4.3</span> (“Effect of Termination”), this
                            <span class="underline">Section 4.4</span> (“Survival”),
                            <span class="underline">Section 5</span> (“Data”),
                            <span class="underline">Section 6</span> (“Ownership; Feedback”),
                            <span class="underline">Section 7</span> (“Representations and Warranties”),
                            <span class="underline">Section 8</span> (“Disclaimer”),
                            <span class="underline">Section 9</span> (“Liability”),
                            <span class="underline">Section 10</span> (“Indemnification”),
                            <span class="underline">Section 11</span> (“Confidentiality”),
                            <span class="underline">Section 12</span> (“Assignment”),
                            <span class="underline">Section 15</span> (“Governing Law; Binding Arbitration; Class Action
                            Waiver; Equitable Relief and Forum Selection”) and
                            <span class="underline">Section 16</span> (“Miscellaneous”).
                        </li>
                    </ul>
                </li>
            </ul>

            <ul>
                <li>
                    <em>5. Data.</em>
                    <ul>
                        <li>
                            <strong>5.1. Device Data.</strong> Subject to the terms and conditions of the Agreement, you
                            hereby grant us a non- exclusive, worldwide, fully paid-up, royalty-free right and license,
                            with the right to grant sublicenses through multiple tiers to vendors providing services to
                            us (such as hosting providers), to reproduce, execute, use, store, archive, modify, perform,
                            display, and distribute the data uploaded by you to the Portal from your Devices and
                            otherwise collected by use through the Services (collectively, "<strong>Device Data</strong
                            >") only for the purposes of (a) providing our products and services to you and (b) in an
                            anonymized, aggregated form, for the purpose of improving our products and services. Upon
                            any termination of the Agreement, we may retain an archival copy of the Device Data kept in
                            the normal course of business or for purposes of complying with applicable laws, rules and
                            regulations and for use in an anonymized, aggregated form, for the purpose of improving our
                            products and services.
                        </li>
                        <li>
                            5.2. Privacy Policy. Our collection, processing, storage, transfer, and use of any personal
                            information included in the Device Data will at all times comply with our Privacy Policy,
                            found here gotenna.com/pages/privacy-policy-apps, and all applicable laws.
                        </li>
                    </ul>
                </li>
            </ul>

            <ul>
                <li>
                    <em>6. Ownership.</em>
                    <ul>
                        <li>
                            <strong>6.1.</strong> As between Customer and goTenna, all right, title, and interest in and
                            to the Services, the RP Software, any documentation and instructions related thereto, the
                            Portal, and all modifications, improvements, adaptations, enhancements, derivatives, and
                            translations made thereto or therefrom, and all intellectual property rights therein
                            (collectively, “goTenna Intellectual Property”) are the exclusive property of goTenna. In
                            addition, the trademarks, service marks, and logos of goTenna are registered and
                            unregistered trademarks or service marks of goTenna and are deemed goTenna Intellectual
                            Property hereunder. Except as expressly granted in the Agreement, nothing herein should be
                            construed as granting, by implication, estoppel, or otherwise, any license or right to use
                            the goTenna Intellectual Property, without our prior written permission specific for each
                            such use. As between Customer and goTenna, Customer will retain all right, title, and
                            interest in and to the Device Data.
                        </li>
                        <li>
                            <strong>6.2. Feedback.</strong> You or your Authorized Users may elect to provide us with
                            feedback, comments, and suggestions with respect to the Devices, the Portal, the Services or
                            the RP Software (“Feedback”). You agree that we (and our successors and assigns) will at all
                            times be free to use, reproduce, disclose, and otherwise exploit any and all such Feedback
                            without compensation or attribution to you or any Authorized Users.
                        </li>
                    </ul>
                </li>
            </ul>

            <ul>
                <li>
                    <em>7. Representations and Warranties.</em>
                    <ul>
                        <li>
                            <strong>7.1. Mutual Representations and Warranties.</strong> Each Party represents and
                            warrants to the other Party that: (a) it is duly organized, validly existing, and in good
                            standing under its jurisdiction of organization and has the right to enter into the
                            Agreement; (b) the execution, delivery, and performance of the Agreement, and the
                            consummation of the transactions contemplated hereby are within the corporate powers of such
                            Party and have been duly authorized by all necessary corporate action on the part of such
                            Party, and constitute a valid and binding agreement of such Party; (c) it has the full
                            power, authority, and right to perform its obligations hereunder; and (d) its performance
                            under the Agreement will comply with all applicable laws, rules and regulations.
                        </li>
                        <li>
                            <strong>7.2. Customer’s Representations and Warranties.</strong> In addition to the above,
                            Customer represents and warrants to goTenna that it has the full right, power, and authority
                            to collect, process, store, and transmit the Device Data, and to grant the licenses to the
                            Device Data granted hereunder, including, without limitation, any information contained in
                            the Device Data about the individual end users of the Devices (each, an “End User”), which
                            may or may not be publically available, that can be used to identify or locate an
                            individual, or any other information that is linked or linkable to an individual, such as
                            first and last name, mailing address and zip code, telephone number, email address or
                            internet protocol (IP) addresses. Without limiting the generality of the foregoing, Customer
                            represents and warrants that it has obtained all necessary consents from its End Users to
                            collect, process, store, transmit, and sublicense the Device Data under all applicable
                            privacy and data protection laws, rules, and regulations.
                        </li>
                    </ul>
                </li>
            </ul>

            <ul>
                <li>
                    <em>8. Disclaimer.</em>
                    <ul>
                        <li>
                            CUSTOMER ACKNOWLEDGES THAT THE PORTAL, THE SERVICES, THE RP SOFTWARE, AND ANY OTHER ITEMS
                            MADE AVAILABLE BY GOTENNA HEREUNDER ARE PROVIDED “AS IS” AND “AS AVAILABLE,” AND NEITHER
                            GOTENNA (NOR OUR SUPPLIERS) MAKES ANY REPRESENTATIONS OR WARRANTIES WITH RESPECT TO THE SAME
                            OR OTHERWISE IN CONNECTION WITH THIS AGREEMENT OR ANY ORDER FORM, AND HEREBY DISCLAIMS ANY
                            AND ALL EXPRESS, IMPLIED, OR STATUTORY WARRANTIES, INCLUDING, WITHOUT LIMITATION, ANY
                            WARRANTIES OF NON-INFRINGEMENT, MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
                            AVAILABILITY, ERROR-FREE OR UNINTERRUPTED OPERATION, AND ANY WARRANTIES ARISING FROM A
                            COURSE OF DEALING, COURSE OF PERFORMANCE, OR USAGE OF TRADE. TO THE EXTENT THAT GOTENNA (AND
                            OUR SUPPLIERS) MAY NOT AS A MATTER OF APPLICABLE LAW DISCLAIM ANY IMPLIED WARRANTY, THE
                            SCOPE AND DURATION OF SUCH WARRANTY WILL BE THE MINIMUM PERMITTED UNDER SUCH LAW.
                        </li>
                    </ul>
                </li>
            </ul>

            <ul>
                <li>
                    <em>9. Liability.</em>
                    <ul>
                        <li>
                            EXCEPT IN CONNECTION WITH CUSTOMER’S FAILURE TO PAY ANY AMOUNTS DUE AND OWING HEREUNDER, A
                            PARTY’S GROSS NEGLIGENCE OR WILLFUL MISCONDUCT, A PARTY’S BREACH OF ITS CONFIDENTIALITY
                            OBLIGATIONS HEREUNDER OR CUSTOMER’S INDEMNITY AND DEFENSE OBLIGATIONS HEREUNDER: (I) IN NO
                            EVENT WILL EITHER PARTY BE LIABLE TO THE OTHER PARTY OR ANY THIRD PARTY FOR ANY INCIDENTAL,
                            INDIRECT, CONSEQUENTIAL, SPECIAL, EXEMPLARY, OR PUNITIVE DAMAGES OF ANY KIND (INCLUDING, BUT
                            NOT LIMITED TO, LOST REVENUES OR PROFITS) ARISING FROM OR RELATING TO THE AGREEMENT, THE
                            PORTAL, THE SERVICES OR THE RF SOFTWARE REGARDLESS OF WHETHER SUCH PARTY WAS ADVISED, HAD
                            OTHER REASON TO KNOW, OR IN FACT KNEW OF THE POSSIBILITY THEREOF; AND (II) EACH PARTY’S
                            AGGREGATE LIABILITY FOR DIRECT DAMAGES UNDER THE AGREEMENT, OR RELATED TO THE PORTAL, THE
                            SERVICES OR THE RF SOFTWARE, WILL NOT EXCEED THE GREATER OF (A) ONE HUNDRED U.S. DOLLARS
                            ($100) OR (B) THE FEES PAID BY CUSTOMER TO GOTENNA HEREUNDER (IF ANY) FOR PREMIUM SERVICES
                            DURING THE PERIOD TWELVE (12) MONTHS PRIOR TO THE EVENT GIVING RISE TO THE FIRST CLAIM. NO
                            ACTION, REGARDLESS OF FORM, ARISING FROM OR PERTAINING TO THE AGREEMENT, THE PORTAL, THE
                            SERVICES OR THE RF SOFTWARE, MAY BE BROUGHT BY YOU MORE THAN ONE (1) YEAR AFTER SUCH ACTION
                            HAS ACCRUED.
                        </li>
                    </ul>
                </li>
            </ul>

            <ul>
                <li>
                    <em>10. Indemnification.</em>
                    <ul>
                        <li>
                            You agree to indemnify, defend, and hold goTenna, our Affiliates, and our and their
                            respective officers, directors, employees, contractors, and agents (“goTenna Indemnitees”)
                            harmless from and against any and all awards, settlement payments, fines and costs of
                            defense (including reasonable attorney’s fees) incurred by any of the goTenna Indemnitees in
                            connection with any action, claim, proceeding or investigation by any regulatory authority
                            or other third party, to the extent arising from or related to (a) your or any of your
                            Authorized Users’ failure to comply with any applicable laws, regulations and rules
                            governing your Devices’ use of the radio frequencies that you set for your Devices, or
                            failure to maintain all required FCC and other licenses to use such radio frequencies or (b)
                            any breach of this Agreement by you.
                        </li>
                    </ul>
                </li>
            </ul>

            <ul>
                <li>
                    <em>11. Confidentiality.</em>
                    <ul>
                        <li>
                            <strong>11.1. Confidentiality.</strong> Any non-public information that one Party (the
                            “Receiving Party”) receives from the other Party (the “Disclosing Party”) in connection with
                            these Terms of Use is proprietary and confidential information (“Confidential Information”),
                            including, without limitation, any information that is marked as “confidential” or should be
                            reasonably understood to be confidential or proprietary to the Disclosing Party. The
                            Receiving Party agrees that it will not disclose the Disclosing Party’s Confidential
                            Information to any third party, nor use the Confidential Information for any purpose other
                            than in the performance of its obligations or the exercise of its rights hereunder. The
                            Receiving Party further agrees to use commercially reasonable measures to maintain the
                            confidence of the Disclosing Party’s Confidential Information. The obligations set forth in
                            this paragraph will not apply to information that the Receiving Party can document is
                            generally available to the public (other than through its breach of the Agreement), was
                            independently developed by the Receiving Party without reference to the Disclosing Party’s
                            information, or was already lawfully in the Receiving Party’s possession at the time of
                            receipt of the information from the Disclosing Party.
                        </li>
                        <li>
                            <strong>11.2. Specific Performance and Injunctive Relief.</strong> The Receiving Party
                            acknowledges that in the event of a breach of Section 11.1 by the Receiving Party,
                            substantial injury could result to the Disclosing Party and money damages will not be a
                            sufficient remedy for such breach. Therefore, in the event that the Receiving Party engages
                            in, or threatens to engage in any act which violates Section 11.1, the Disclosing Party will
                            be entitled, in addition to all other remedies which may be available to it under law, to
                            seek injunctive relief (including, without limitation, temporary restraining orders, or
                            preliminary or permanent injunctions) and specific enforcement of the terms of Section 11.1.
                            The Disclosing Party will not be required to post a bond or other security in connection
                            with the granting of any such relief.
                        </li>
                    </ul>
                </li>
            </ul>

            <ul>
                <li>
                    <em>12. Assignment.</em>
                    <ul>
                        <li>
                            The Agreement may not be assigned or transferred by either Party without the prior, written
                            consent of the other Party; provided, however, that such consent will not be required in
                            connection with an assignment or transfer of this Agreement by either Party (a) to any of
                            its Affiliates; or (b) in connection with any merger, consolidation, sale of equity
                            interests, sale of all or substantially all assets, or other change of control transaction
                            involving such Party or its business. Any attempted assignment that does not comply with the
                            terms of this Section will be null and void.
                        </li>
                    </ul>
                </li>
            </ul>

            <ul>
                <li>
                    <em>13. U.S. Government Restricted Rights.</em>
                    <ul>
                        <li>
                            If the RF Software or any other software associated with the Devices, the Portal or the
                            Services are to be used by an agent or agency of the U.S. Government, then such software and
                            related documentation made available to Customer shall be “commercial computer software” and
                            “commercial computer software documentation,” respectively, as such terms are used in 48
                            C.F.R. 12.212 of the Federal Acquisition Regulations (“FAR”) and its successors and 48
                            C.F.R. 227.7202 of the Department of Defense FAR Supplement (“DFARS”) and its successors. In
                            accordance with FAR 12.212 or DFARS 227.7202, as applicable, any such software and
                            documentation are made available to U.S. Government end users with only those rights set
                            forth in the Agreement.
                        </li>
                    </ul>
                </li>
            </ul>

            <ul>
                <li>
                    <em>14. Export Controls.</em>
                    <ul>
                        <li>
                            Customer will ensure that any exportation of any software associated with the Devices, the
                            Portal or the Services by or on behalf of Customer or any of its Affiliates is in accordance
                            with any applicable United States and foreign export control laws. We will provide Customer
                            with such information regarding such software as may be reasonably requested by Customer in
                            its efforts to comply with the foregoing.
                        </li>
                    </ul>
                </li>
            </ul>

            <ul>
                <li>
                    <em>
                        15. Governing Law; Binding Arbitration; Class Action Waiver; Equitable Relief and Forum
                        Selection.
                    </em>
                    <ul>
                        <li>
                            <strong>15.1. Governing Law.</strong> The Agreement, and any and all disputes directly or
                            indirectly arising out of or relating to the Agreement, will be governed by and construed in
                            accordance with the laws of the State of New York, without reference to the choice of law
                            rules thereof.
                        </li>
                        <li>
                            <strong>15.2. Binding Arbitration.</strong> In the event of a dispute arising under or
                            relating to this Agreement, the RP Software, the Portal, the Services and/or the Devices
                            (each, a “Dispute”), either Party may elect to finally and exclusively resolve the dispute
                            by binding arbitration governed by the Federal Arbitration Act (“FAA”). Any election to
                            arbitrate, at any time, shall be final and binding on the other Party. IF EITHER PARTY
                            CHOOSES ARBITRATION, NEITHER PARTY SHALL HAVE THE RIGHT TO LITIGATE SUCH CLAIM IN COURT OR
                            TO HAVE A JURY TRIAL. ARBITRATION IS DIFFERENT FROM COURT, AND DISCOVERY AND APPEAL RIGHTS
                            MAY ALSO BE LIMITED IN ARBITRATION. All disputes will be resolved before a neutral
                            arbitrator selected jointly by the Parties, whose decision will be final, except for a
                            limited right of appeal under the FAA. The arbitration shall be commenced and conducted by
                            JAMS pursuant to its then current Comprehensive Arbitration Rules and Procedures and in
                            accordance with the Expedited Procedures in those rules, or, where appropriate, pursuant to
                            JAMS’ Streamlined Arbitration Rules and Procedures. All applicable JAMS’ rules and
                            procedures are available at the JAMS website www.jamsadr.com. Each Party will be responsible
                            for paying any JAMS filing, administrative, and arbitrator fees in accordance with JAMS
                            rules. Judgment on the arbitrator’s award may be entered in any court having jurisdiction.
                            This clause shall not preclude Parties from seeking provisional remedies in aid of
                            arbitration from a court of appropriate jurisdiction. The arbitration shall take place in
                            Kings County, New York. The Parties may litigate in court to compel arbitration, to stay a
                            proceeding pending arbitration, or to confirm, modify, vacate, or enter judgment on the
                            award entered by the arbitrator. The Parties shall cooperate in good faith in the voluntary
                            and informal exchange of all non-privileged documents and other information (including
                            electronically stored information) relevant to the Dispute immediately after commencement of
                            the arbitration. As set forth in Section 15.4 below, nothing in this Agreement will prevent
                            us from seeking injunctive relief in any court of competent jurisdiction as necessary to
                            protect our proprietary interests.
                        </li>
                        <li>
                            <strong>15.3. Class Action Waiver.</strong> You agree that any arbitration or proceeding
                            shall be limited to the Dispute between us and you individually. To the full extent
                            permitted by law, (i) no arbitration or proceeding shall be joined with any other; (ii)
                            there is no right or authority for any Dispute to be arbitrated or resolved on a class
                            action-basis or to utilize class action procedures; and (iii) there is no right or authority
                            for any Dispute to be brought in a purported representative capacity on behalf of the
                            general public or any other persons. YOU AGREE THAT YOU MAY BRING CLAIMS AGAINST US ONLY IN
                            YOUR INDIVIDUAL CAPACITY AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS OR
                            REPRESENTATIVE PROCEEDING.
                        </li>
                        <li>
                            <strong>15.4. Equitable Relief.</strong> You acknowledge and agree that in the event of a
                            breach or threatened violation of our intellectual property rights and/or confidential and
                            proprietary information by you, we will suffer irreparable harm and will therefore be
                            entitled to injunctive relief to enforce this Agreement. We may, without waiving any other
                            remedies under this Agreement, seek from any court having jurisdiction any interim,
                            equitable, provisional, or injunctive relief that is necessary to protect our rights and
                            property pending the outcome of the arbitration referenced above.
                        </li>
                        <li>
                            <strong>15.5. Forum Selection.</strong> To the extent Section 15.2 does not apply (e.g.,
                            because neither Party elects to have a Dispute resolved by binding arbitration pursuant to
                            Section 15.2, or in cases where Section 15.4 applies), then each of the Parties hereby
                            irrevocably consents and submits to the exclusive jurisdiction of the state and federal
                            courts located in Kings County, New York for such Disputes, and waives any objections to the
                            laying of venue in such courts.
                        </li>
                    </ul>
                </li>
            </ul>

            <ul>
                <li>
                    <em>16. Miscellaneous.</em>
                    <ul>
                        <li>
                            The relationship of the Parties is one of independent contractors. The Agreement (including
                            all documents incorporated by reference herein) sets forth the entire agreement of the
                            Parties as to its subject matter and supersedes all prior agreements, negotiations,
                            representations, and promises between the Parties with respect to the subject matter hereof.
                            The Agreement is binding upon and will inure to the benefit of each of the Parties and their
                            respective successors and permitted assigns. We may revise these Terms of Use from time to
                            time by posting the revisions to the Terms of Use on the Portal. By continuing to access or
                            use the Portal, the Services or the RF Software after revisions become effective, you agree
                            to be bound by the revised Terms of Use; provided, however, that the revised version of the
                            Terms of Use will not apply to any Premium Service subscriptions that are in effect when the
                            revised version of the Terms of Use is posted on the Portal (but the revised version of the
                            Terms of Use will apply to any Premium Service subscriptions that are purchased after the
                            revised version of the Terms of Use is posted on the Portal). A waiver of rights under the
                            Agreement will not be effective unless it is in writing and signed by an authorized
                            representative of the Party that is waiving the rights. The Parties agree that there are no
                            third-party beneficiaries under the Agreement.
                        </li>
                    </ul>
                </li>
            </ul>

            <p>
                <strong>
                    CUSTOMER ACKNOWLEDGES THAT CUSTOMER HAS READ THESE TERMS OF USE, UNDERSTANDS THEM, AND AGREES TO BE
                    BOUND BY THEIR TERMS AND CONDITIONS.
                </strong>
            </p>
        </div>

        <button
            class="btn terms-of-service__accept"
            :class="{ active: submitted }"
            :disabled="!termsRead"
            @click="accept"
        >
            <div>Accept</div>
        </button>
    </div>
</template>

<script>
export default {
    props: ['token'],

    data() {
        return {
            termsRead: false,
            submitted: false,
        }
    },

    mounted() {
        this.$emit('step', 3)
    },

    methods: {
        accept() {
            if (this.termsRead && !this.submitted) {
                this.submitted = true

                this.$store
                    .dispatch('acceptTerms')
                    .then(() => {
                        this.$router.push('/')
                    })
                    .catch((error) => {
                        //
                    })
            }
        },

        checkTermsRead(e) {
            const terms = e.target

            if (Math.ceil(terms.scrollTop) >= terms.scrollHeight - terms.offsetHeight) {
                this.termsRead = true
            }
        },
    },
}
</script>
